import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ADPResourceService } from 'digital-core';
import { AppConstants } from 'src/app/core/constants';
import { LoggedInUserSettings } from 'src/app/core/services/settings/logged-in-user-settings';
import { daysUntil } from 'src/app/core/utils/plan-coverage-days.util';
import { getMaxThresholdDays } from 'src/app/core/utils/renewal-notice-thresholds.util';

@Injectable({ providedIn: 'root' })
export class ServiceRequestVisibilityService {
  private resourceService = inject(ADPResourceService);
  private loggedInUserSettings = inject(LoggedInUserSettings);

  private selectedPlanDetail = toSignal(this.loggedInUserSettings.$selectedPlanDetail, { initialValue: null });
  private plans = toSignal(this.loggedInUserSettings.$plans, { initialValue: null });
  private limitedMode = toSignal(this.loggedInUserSettings.$limitedMode, { initialValue: false });

  private readonly afxRenewal = toSignal(this.loggedInUserSettings.$afxRenewal, { initialValue: null });

  private readonly coverageMinDays = computed(() => {
    const setting = this.resourceService.getPortalSetting('Global_Renewal_Coverage_Min_Days');
    return parseInt(setting?.Value ?? '0', 10);
  });

  // No Global_Renewal_Coverage_Max_Days setting here: this is the one caller with no per-context
  // override layer, so it derives its max days straight from the largest day threshold in
  // Global_Renewal_Notice_Thresholds (same source plan-renewal.service.ts falls back to).
  private readonly coverageMaxDays = computed(() => {
    const thresholds = this.resourceService.getPortalLabel('Global_Renewal_Notice_Thresholds')?.Value;
    return getMaxThresholdDays(thresholds);
  });

  private readonly isRenewPlanEnabled = computed(() => this.afxRenewal()?.isOfferAvailable ?? false);

  private readonly coverageEndsDays = computed(() => {
    const planDetail = this.selectedPlanDetail();
    if (!planDetail) {
      return this.coverageMaxDays() + 1;
    }
    const planCoreConst = this.resourceService.getPortalLabel('Global_Core_Plan_Details_Prefix')?.Value;
    if (!planCoreConst) {
      return this.coverageMaxDays() + 1;
    }
    const coreCertificate = planDetail.certificates?.find((c) => c.certificateNumber.startsWith(planCoreConst));
    if (!coreCertificate) {
      return this.coverageMaxDays() + 1;
    }
    const endDate = new Date(coreCertificate.certificateSalesDate);
    endDate.setMonth(endDate.getMonth() + planDetail.planTerm);
    const remainingDays = daysUntil(endDate);
    if (coreCertificate.status?.toLowerCase() === AppConstants.PLAN_STATUS.Expired.toLowerCase() && remainingDays > 0) {
      return 0;
    }
    return remainingDays;
  });

  // Account-wide, not tied to whichever plan happens to be selected while browsing: this button
  // starts a brand-new request flow independent of the current page, so it should only hide when
  // EVERY plan on the account is cancelled, not just the one currently in view (otherwise viewing a
  // cancelled plan's details hides the global entry point even when another plan is still active).
  private readonly allPlansCancelled = computed(() => {
    const plans = this.plans();
    if (!plans?.length) {
      return false;
    }
    return plans.every((plan) => plan.status?.toLowerCase() === AppConstants.PLAN_STATUS.Cancelled.toLowerCase());
  });

  readonly isHidden = computed<boolean>(() => {
    if (this.limitedMode()) {
      return true;
    }
    if (this.allPlansCancelled()) {
      return true;
    }
    return this.isRenewPlanEnabled() && this.coverageEndsDays() <= this.coverageMinDays();
  });
}
